import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Rates, Orders, auth, api } from "../lib/api.js";

const C = {
  navy: "#0E1A2E", navyDeep: "#070D1B", red: "#C8302A",
  amber: "#E8A317", amberPale: "#FFF6D9",
  green: "#0F8B3D", greenPale: "#E8F5E9",
  white: "#FFFFFF", cream: "#FAF7F1", sand: "#F0EADB",
  border: "#E0D9C8", line: "#EBE4D2",
  text: "#1A1A1A", text2: "#3A3A3A", text3: "#6A6A6A", text4: "#9A9A9A",
};

export default function BookShipment() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [pickupAddresses, setPickupAddresses] = useState([]);

  const [form, setForm] = useState({
    pickup_address_id: "",
    receiver_name: "",
    receiver_phone: "",
    receiver_address: "",
    receiver_city: "",
    receiver_state: "",
    receiver_pincode: "",
    weight_grams: 500,
    payment_mode: "prepaid",
    cod_amount_paise: 0,
  });

  const [rates, setRates] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!auth.loggedIn()) {
      navigate("/login");
      return;
    }
    // Load merchant's pickup addresses
    api.get("/pickups/addresses").then((addrs) => {
      const list = Array.isArray(addrs) ? addrs : (addrs?.addresses || []);
      setPickupAddresses(list);
      if (list.length > 0) {
        setForm((f) => ({ ...f, pickup_address_id: list[0].id }));
      }
    }).catch((err) => {
      // Couldn't load addresses — usually means none added yet
      console.warn("Couldn't load pickup addresses:", err.message);
    });
  }, []); // eslint-disable-line

  const update = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const fetchRates = async () => {
    setError(null);
    setBusy(true);
    try {
      const pickup = pickupAddresses.find((a) => a.id === form.pickup_address_id);
      if (!pickup) {
        setError("Please add a pickup address first.");
        return;
      }
      const result = await Rates.calculate({
        origin_pincode: pickup.pincode,
        destination_pincode: form.receiver_pincode,
        weight_grams: form.weight_grams,
        payment_mode: form.payment_mode,
        cod_amount_paise: form.cod_amount_paise,
      });
      setRates(result);
      const recommended = result.quotes?.find((q) => q.is_recommended) || result.quotes?.[0];
      setSelectedQuote(recommended);
      setStep(2);
    } catch (err) {
      setError(err.message || "Couldn't fetch rates. Please check the addresses and try again.");
    } finally {
      setBusy(false);
    }
  };

  const createOrder = async () => {
    setError(null);
    setBusy(true);
    try {
      // Step 1: Create the order
      const order = await Orders.create({
        pickup_address_id: form.pickup_address_id,
        receiver_name: form.receiver_name,
        receiver_phone: form.receiver_phone,
        receiver_address: form.receiver_address || `${form.receiver_city}, ${form.receiver_state}`,
        receiver_city: form.receiver_city,
        receiver_state: form.receiver_state,
        receiver_pincode: form.receiver_pincode,
        weight_grams: form.weight_grams,
        payment_mode: form.payment_mode,
        cod_amount_paise: form.cod_amount_paise,
      });

      // Step 2: Book it with the selected courier and service
      if (selectedQuote) {
        await Orders.book(order.id, selectedQuote.courier_id, selectedQuote.service_code);
      }

      setCreated(order);
      setStep(3);
    } catch (err) {
      setError(err.message || "Couldn't create order. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const noPickup = pickupAddresses.length === 0;

  return (
    <div style={{
      minHeight: "100vh", background: C.cream,
      fontFamily: "-apple-system, system-ui, Inter, sans-serif",
    }}>
      {/* Top bar */}
      <div style={{
        background: C.white, borderBottom: `1px solid ${C.line}`, padding: "16px 24px",
      }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Link to="/dashboard" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 32, height: 32, background: C.red, borderRadius: 8,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: C.white, fontWeight: 800, fontSize: 16, letterSpacing: -1,
            }}>S</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: C.navy, letterSpacing: -0.5 }}>Shiftswift</div>
          </Link>
          <Link to="/dashboard" style={{ fontSize: 13, color: C.text2, textDecoration: "none", fontWeight: 600 }}>
            ← Back to dashboard
          </Link>
        </div>
      </div>

      {/* Stepper */}
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "32px 24px 0" }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
          {[
            { n: 1, l: "Details" },
            { n: 2, l: "Choose service" },
            { n: 3, l: "Done" },
          ].map((s) => (
            <div key={s.n} style={{ flex: 1 }}>
              <div style={{
                height: 4, borderRadius: 2,
                background: step >= s.n ? C.red : C.line,
                marginBottom: 6,
              }} />
              <div style={{
                fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
                color: step >= s.n ? C.navy : C.text4,
              }}>
                {s.n}. {s.l}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "0 24px 60px" }}>
        {error && (
          <div style={{
            background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B",
            padding: "12px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16,
          }}>
            {error}
          </div>
        )}

        {/* STEP 1 — Details */}
        {step === 1 && (
          <div style={{
            background: C.white, borderRadius: 14, padding: 32,
            border: `1px solid ${C.line}`,
          }}>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: C.navy, margin: 0, letterSpacing: -0.6 }}>
              Book a shipment
            </h1>
            <p style={{ fontSize: 14, color: C.text3, marginTop: 6, marginBottom: 24 }}>
              Where is this parcel going?
            </p>

            {noPickup ? (
              <div style={{
                background: C.amberPale, border: `1px solid ${C.amber}`, color: C.navy,
                padding: 16, borderRadius: 10, marginBottom: 18,
              }}>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>
                  Add a pickup address first
                </div>
                <div style={{ fontSize: 13, color: C.text2, marginBottom: 12 }}>
                  We need to know where to pick up your parcels.
                </div>
                <Link to="/dashboard" style={{
                  display: "inline-block", background: C.red, color: C.white,
                  padding: "8px 14px", borderRadius: 6,
                  textDecoration: "none", fontSize: 12, fontWeight: 700,
                }}>
                  Add pickup address →
                </Link>
              </div>
            ) : (
              <div style={{ marginBottom: 14 }}>
                <Label>Pickup from</Label>
                <select
                  value={form.pickup_address_id}
                  onChange={(e) => update("pickup_address_id")(e.target.value)}
                  style={inputStyle}
                >
                  {pickupAddresses.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.label || a.contact_name} — {a.city} ({a.pincode})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Receiver name" value={form.receiver_name} onChange={update("receiver_name")} placeholder="Priya Sharma" autoFocus />
              <Field label="Receiver phone" value={form.receiver_phone} onChange={update("receiver_phone")} placeholder="+91 98765 43210" />
            </div>

            <Field label="Address (optional)" value={form.receiver_address} onChange={update("receiver_address")} placeholder="42 Marine Drive, Fort" />

            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12 }}>
              <Field label="City" value={form.receiver_city} onChange={update("receiver_city")} placeholder="Mumbai" />
              <Field label="State" value={form.receiver_state} onChange={update("receiver_state")} placeholder="MH" />
              <Field label="PIN code" value={form.receiver_pincode} onChange={update("receiver_pincode")} placeholder="400001" maxLength={6} />
            </div>

            <Field label="Weight (grams)" type="number" value={form.weight_grams} onChange={(v) => update("weight_grams")(parseInt(v) || 0)} placeholder="500" />

            <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <button
                type="button"
                onClick={() => update("payment_mode")("prepaid")}
                style={modeButton(form.payment_mode === "prepaid")}
              >
                <strong>Prepaid</strong>
                <div style={{ fontSize: 11, marginTop: 2, opacity: 0.85 }}>Buyer paid online</div>
              </button>
              <button
                type="button"
                onClick={() => update("payment_mode")("cod")}
                style={modeButton(form.payment_mode === "cod")}
              >
                <strong>Cash on Delivery</strong>
                <div style={{ fontSize: 11, marginTop: 2, opacity: 0.85 }}>Collect on delivery</div>
              </button>
            </div>

            {form.payment_mode === "cod" && (
              <div style={{ marginTop: 14 }}>
                <Field label="COD amount to collect (₹)" type="number"
                  value={form.cod_amount_paise / 100}
                  onChange={(v) => update("cod_amount_paise")(Math.round((parseFloat(v) || 0) * 100))}
                  placeholder="999" />
              </div>
            )}

            <button
              onClick={fetchRates}
              disabled={busy || noPickup || !form.receiver_name || !form.receiver_pincode || form.weight_grams <= 0}
              style={{
                width: "100%", marginTop: 24,
                background: busy ? C.text4 : C.red, color: C.white,
                border: "none", padding: 14, borderRadius: 8,
                fontSize: 15, fontWeight: 700,
                cursor: busy ? "not-allowed" : "pointer",
                fontFamily: "inherit",
              }}
            >
              {busy ? "Getting rates..." : "Get rates →"}
            </button>
          </div>
        )}

        {/* STEP 2 — Choose service */}
        {step === 2 && rates && (
          <div style={{
            background: C.white, borderRadius: 14, padding: 32,
            border: `1px solid ${C.line}`,
          }}>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: C.navy, margin: 0, letterSpacing: -0.6 }}>
              Choose a service
            </h1>
            <p style={{ fontSize: 14, color: C.text3, marginTop: 6, marginBottom: 20 }}>
              {rates.zone_label && `Zone: ${rates.zone_label} · `}
              Billable weight: {rates.billable_weight_grams || form.weight_grams}g
            </p>

            <div>
              {(rates.quotes || []).map((q, i) => {
                const isSelected = selectedQuote?.service_code === q.service_code;
                return (
                  <button
                    key={i}
                    onClick={() => setSelectedQuote(q)}
                    style={{
                      width: "100%",
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "16px 18px", marginBottom: 10,
                      background: isSelected ? C.amberPale : C.white,
                      border: `2px solid ${isSelected ? C.amber : C.line}`,
                      borderRadius: 10, cursor: "pointer", textAlign: "left",
                      fontFamily: "inherit",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: C.navy }}>
                        {q.service_name === "Speed Post" ? "Express" : q.service_name === "Business Parcel" ? "Standard" : q.service_name}
                        {q.is_recommended && (
                          <span style={{ marginLeft: 10, fontSize: 9, color: C.red, fontWeight: 800, letterSpacing: 0.5 }}>
                            RECOMMENDED
                          </span>
                        )}
                        {q.is_cheapest && !q.is_recommended && (
                          <span style={{ marginLeft: 10, fontSize: 9, color: C.green, fontWeight: 800, letterSpacing: 0.5 }}>
                            BEST VALUE
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: C.text3, marginTop: 4 }}>
                        {q.estimated_days} {q.estimated_days === 1 ? "day" : "days"} delivery
                        {q.courier_name ? ` · via ${q.courier_name}` : ""}
                      </div>
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: C.navy, letterSpacing: -0.5 }}>
                      ₹{q.total_rs}
                    </div>
                  </button>
                );
              })}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 20 }}>
              <button
                onClick={() => setStep(1)}
                style={{
                  background: "transparent", color: C.text2,
                  border: `1px solid ${C.border}`, padding: 14, borderRadius: 8,
                  fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                }}
              >
                ← Edit details
              </button>
              <button
                onClick={createOrder}
                disabled={busy || !selectedQuote}
                style={{
                  background: busy ? C.text4 : C.red, color: C.white,
                  border: "none", padding: 14, borderRadius: 8,
                  fontSize: 14, fontWeight: 700,
                  cursor: busy ? "not-allowed" : "pointer", fontFamily: "inherit",
                }}
              >
                {busy ? "Booking..." : `Book for ₹${selectedQuote?.total_rs || "—"}`}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3 — Confirmation */}
        {step === 3 && created && (
          <div style={{
            background: C.white, borderRadius: 14, padding: 32,
            border: `1px solid ${C.line}`, textAlign: "center",
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: 28,
              background: C.green, color: C.white,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              fontSize: 26, fontWeight: 800, marginBottom: 16,
            }}>
              ✓
            </div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: C.navy, margin: 0, letterSpacing: -0.6 }}>
              Shipment booked
            </h1>
            <p style={{ fontSize: 14, color: C.text3, marginTop: 6 }}>
              Your AWB will be available in your dashboard shortly.
            </p>

            <div style={{
              background: C.cream, borderRadius: 10, padding: 20, marginTop: 24, textAlign: "left",
            }}>
              <Row label="Order #" value={created.order_number} />
              <Row label="To" value={`${form.receiver_name}, ${form.receiver_city}`} />
              <Row label="Service" value={selectedQuote?.service_name === "Speed Post" ? "Express" : selectedQuote?.service_name === "Business Parcel" ? "Standard" : selectedQuote?.service_name || "—"} />
              <Row label="Amount" value={`₹${selectedQuote?.total_rs || "—"}`} last />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 24 }}>
              <Link to="/dashboard" style={{
                background: "transparent", color: C.text2,
                border: `1px solid ${C.border}`, padding: 14, borderRadius: 8,
                fontSize: 14, fontWeight: 700, textDecoration: "none",
                textAlign: "center",
              }}>
                Back to dashboard
              </Link>
              <button onClick={() => {
                setStep(1); setCreated(null); setRates(null); setSelectedQuote(null);
                setForm({
                  pickup_address_id: pickupAddresses[0]?.id || "",
                  receiver_name: "", receiver_phone: "", receiver_address: "",
                  receiver_city: "", receiver_state: "", receiver_pincode: "",
                  weight_grams: 500, payment_mode: "prepaid", cod_amount_paise: 0,
                });
              }} style={{
                background: C.red, color: C.white,
                border: "none", padding: 14, borderRadius: 8,
                fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
              }}>
                Book another
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "11px 14px", border: `1px solid ${C.border}`,
  borderRadius: 7, fontSize: 14, boxSizing: "border-box",
  background: C.cream, fontFamily: "inherit", outline: "none",
};

function Label({ children }) {
  return (
    <label style={{ fontSize: 12, color: C.text2, fontWeight: 600, display: "block", marginBottom: 6 }}>
      {children}
    </label>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, autoFocus, maxLength }) {
  // Stable input — no inline DOM mutation
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: 12 }}>
      <Label>{label}</Label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        maxLength={maxLength}
        style={{
          ...inputStyle,
          borderColor: focused ? C.red : C.border,
          background: focused ? C.white : C.cream,
          boxShadow: focused ? `0 0 0 3px ${C.red}15` : "none",
          transition: "border-color 0.15s, background 0.15s",
        }}
      />
    </div>
  );
}

function modeButton(active) {
  return {
    padding: "12px 14px", border: `2px solid ${active ? C.red : C.line}`,
    borderRadius: 8, background: active ? C.red : C.white,
    color: active ? C.white : C.text, cursor: "pointer",
    textAlign: "left", fontSize: 14, fontFamily: "inherit",
  };
}

function Row({ label, value, last }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between",
      padding: "8px 0",
      borderBottom: last ? "none" : `1px solid ${C.line}`,
    }}>
      <span style={{ fontSize: 13, color: C.text3 }}>{label}</span>
      <strong style={{ fontSize: 13, color: C.navy }}>{value}</strong>
    </div>
  );
}
